import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError, type PlayerPublicTournament, type PlayerPublicTournamentsResponse } from "../lib/api";
import { sportLabel } from "../lib/labels";
import { tournamentTypeLabel } from "../lib/playerLabels";
import { categoriesDeadlineSummary, formatDirectoryDates, formatPriceFrom } from "../lib/publicDirectory";

type DirectoryData = PlayerPublicTournamentsResponse["data"];

/** «Torneos públicos» del jugador (Eduardo 2026-09-26): torneos con
 * inscripciones abiertas donde el PERFIL ACTIVO puede inscribirse (rango de
 * edad, rama y cupo), sin los que ya tiene. «Inscribir» abre el link público
 * con ese perfil ya elegido. */
export default function PlayerPublicTournaments({
  token,
  profileId,
}: {
  token: string;
  profileId: number | null;
}) {
  const navigate = useNavigate();
  const [data, setData] = useState<DirectoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Al cambiar de perfil, solo pinta la respuesta más nueva.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    api.player
      .publicTournaments(token, profileId)
      .then((response) => {
        if (seq === requestSeqRef.current) setData(response.data);
      })
      .catch((err) => {
        if (seq !== requestSeqRef.current) return;
        setData(null);
        setError(err instanceof ApiError ? err.message : "No pudimos cargar los torneos públicos.");
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false);
      });
  }, [token, profileId]);

  if (loading) return <p className="muted">Buscando torneos con inscripciones abiertas…</p>;
  if (error) {
    return (
      <p role="alert" className="form-error">
        {error}
      </p>
    );
  }
  const profile = data?.profile ?? null;
  if (!profile) {
    return (
      <p className="muted">
        Tu cuenta no está vinculada a un perfil de jugador. Pídele al admin que te genere un PIN.
      </p>
    );
  }
  const firstName = profile.display_name.split(" ")[0] || profile.display_name;
  const tournaments = data?.tournaments ?? [];
  const hidden = data?.hidden_count ?? 0;

  return (
    <div className="public-offer">
      <p className="muted public-offer__intro">
        {/* En «tú» con el nombre del perfil elegido (cuentas familiares). */}
        Torneos con inscripciones abiertas para ti (<strong>{profile.display_name}</strong>)
        {profile.has_birthdate ? ", solo en las categorías de tu edad." : "."}
      </p>
      {!profile.has_birthdate ? (
        <p className="public-offer__notice" data-no-toast>
          No tenemos tu fecha de nacimiento: te mostramos todos los torneos abiertos. Al inscribirte se valida tu
          categoría.
        </p>
      ) : null}
      {tournaments.length === 0 ? (
        <p className="muted">
          Por ahora no hay torneos abiertos para ti.
          {hidden > 0
            ? ` Hay ${hidden} torneo${hidden === 1 ? "" : "s"} con inscripciones abiertas fuera de tu categoría o en ${hidden === 1 ? "el" : "los"} que ya estás inscrito.`
            : ""}
        </p>
      ) : (
        <div className="tournament-card-grid">
          {tournaments.map((tournament) => (
            <PublicOfferCard
              key={tournament.slug}
              tournament={tournament}
              firstName={firstName}
              onRegister={() =>
                navigate(tournament.registration_url, { state: { playerProfileId: profile.id } })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PublicOfferCard({
  tournament,
  firstName,
  onRegister,
}: {
  tournament: PlayerPublicTournament;
  firstName: string;
  onRegister: () => void;
}) {
  const price = formatPriceFrom(tournament.price_from);
  const deadlineSummary = categoriesDeadlineSummary(tournament.categories ?? [], tournament.registration_deadline);
  return (
    <article className="tournament-card public-offer-card">
      {tournament.poster_url ? (
        <img className="public-offer-card__poster" src={tournament.poster_url} alt={`Póster de ${tournament.name}`} loading="lazy" />
      ) : null}
      <div>
        <p className="tournament-card__type">
          {sportLabel(tournament.sport)} · {tournamentTypeLabel(tournament.tournament_type)}
        </p>
        <h3 className="tournament-card__title">{tournament.name}</h3>
      </div>
      <div className="tournament-card__row">
        <span className="muted">Fechas</span>
        <span>{formatDirectoryDates(tournament.start_date, tournament.end_date)}</span>
      </div>
      {tournament.venue_name ? (
        <div className="tournament-card__row">
          <span className="muted">Sede</span>
          <span>
            {tournament.venue_name}
            {tournament.google_maps_link ? (
              <>
                {" · "}
                <a href={tournament.google_maps_link} target="_blank" rel="noopener noreferrer">
                  Cómo llegar
                </a>
              </>
            ) : null}
          </span>
        </div>
      ) : null}
      {tournament.organizer_name ? (
        <div className="tournament-card__row">
          <span className="muted">Organiza</span>
          <span>{tournament.organizer_name}</span>
        </div>
      ) : null}
      {price ? (
        <div className="tournament-card__row">
          <span className="muted">Costo general</span>
          <span>{price}</span>
        </div>
      ) : null}
      <div className="tournament-card__row">
        <span className="muted">Inscripción</span>
        <span>{tournament.extemporaneous ? "Abierta (extemporánea)" : "Abierta"}</span>
      </div>
      {deadlineSummary?.kind === "single" ? (
        <div className="tournament-card__row">
          <span className="muted">Cierre</span>
          <span>Inscripciones hasta el {formatDirectoryDates(deadlineSummary.deadline, deadlineSummary.deadline)}</span>
        </div>
      ) : deadlineSummary?.kind === "mixed" ? (
        <div className="tournament-card__row public-offer-card__deadlines">
          <span className="muted">Cierre</span>
          <span>
            {deadlineSummary.lines.map((line) => (
              <span key={line} className="public-offer-card__deadline-line">{line}</span>
            ))}
          </span>
        </div>
      ) : null}
      {tournament.eligible_categories.length > 0 ? (
        <div className="public-offer-card__categories">
          <span className="muted">Puedes inscribirte en</span>
          <ul>
            {tournament.eligible_categories.map((category) => (
              <li key={category.id}>{category.name}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {tournament.registered_categories.length > 0 ? (
        <p className="muted public-offer-card__registered">Ya estás inscrito en: {tournament.registered_categories.join(", ")}</p>
      ) : null}
      <div className="tournament-card__actions">
        <button type="button" className="btn btn--compact" onClick={onRegister}>
          Inscribir a {firstName}
        </button>
      </div>
    </article>
  );
}
